import { useState, useEffect } from "react";
import { Link, useLocation, NavLink } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import { AutomexLogo } from "@/components/common/AutomexLogo";
import { NAV } from "@/data/site";

export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white border-b border-slate-200 shadow-sm" : "bg-white/95 backdrop-blur-md border-b border-slate-100"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <div className="flex h-16 md:h-20 items-center justify-between gap-4">
          <Link to="/" data-testid="logo-link" className="shrink-0">
            <AutomexLogo />
          </Link>

          <nav className="hidden xl:flex items-center gap-0.5">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={({ isActive }) =>
                  `px-2.5 py-2 text-[12px] font-semibold uppercase tracking-normal whitespace-nowrap transition-colors ${
                    isActive ? "text-automex-blue" : "text-slate-600 hover:text-automex-blue"
                  }`
                }
                end={item.to === "/"}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden xl:block">
            <Link
              to="/contact"
              data-testid="header-discuss-cta"
              className="group inline-flex items-center gap-2 bg-automex-teal text-automex-navy px-4 py-3 text-[12px] font-bold uppercase tracking-wider rounded-sm hover:bg-[#12b494] transition-colors"
            >
              Discuss Your Project
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            className="xl:hidden p-2 text-automex-navy"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <div
        className={`xl:hidden overflow-hidden transition-[max-height] duration-300 bg-white border-b border-slate-200 ${
          open ? "max-h-[640px]" : "max-h-0"
        }`}
      >
        <nav className="px-5 py-4 flex flex-col" data-testid="mobile-nav">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `py-3 border-b border-slate-100 text-sm font-semibold uppercase tracking-wide ${
                  isActive ? "text-automex-blue" : "text-slate-700"
                }`
              }
              end={item.to === "/"}
            >
              {item.label}
            </NavLink>
          ))}
          <Link
            to="/contact"
            data-testid="mobile-discuss-cta"
            className="mt-4 inline-flex items-center justify-center gap-2 bg-automex-teal text-automex-navy px-5 py-3.5 text-sm font-bold uppercase tracking-wider rounded-sm"
          >
            Discuss Your Project
            <ArrowRight className="h-4 w-4" />
          </Link>
        </nav>
      </div>
    </header>
  );
}
